// ─── API BASE URL ──────────────────────────────────────────────────────────────
// The frontend talks ONLY to the backend REST API.
// The backend (Express/Node) handles all Firebase Firestore & Auth operations.
// Never import firebase SDK directly in the frontend.

// In production, always use /api — Vercel rewrites proxy it to Railway (no CORS).
// In local dev, talk directly to the local backend.
const API_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : '/api';

// ─── TOKEN STORAGE ─────────────────────────────────────────────────────────────
// JWT token stored in localStorage so it survives page reloads and works on
// mobile browsers where cross-origin session cookies are blocked by iOS Safari ITP.
const TOKEN_KEY = 'hk_token';
export const saveToken = (token) => { if (token) localStorage.setItem(TOKEN_KEY, token); };
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
const getToken = () => localStorage.getItem(TOKEN_KEY);

// ─── SHARED FETCH OPTIONS ──────────────────────────────────────────────────────
const defaultOptions = {
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include',
};

// ─── SIMPLE CACHE ─────────────────────────────────────────────────────────────
const cache = new Map();
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes

// ─── HELPER ────────────────────────────────────────────────────────────────────
async function apiFetch(path, options = {}) {
  // Only cache GET requests
  const isCacheable = !options.method || options.method === 'GET';
  const cacheKey = `${path}${JSON.stringify(options.headers || {})}`;

  if (isCacheable && cache.has(cacheKey)) {
    const { timestamp, data } = cache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_TTL) {
      return data;
    }
    cache.delete(cacheKey);
  }

  // Attach JWT token in Authorization header if available (mobile auth)
  const token = getToken();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...authHeader, ...(options.headers || {}) },
  };
  const res = await fetch(`${API_URL}${path}`, mergedOptions);
  if (!res.ok) {
    let errObj = {};
    try {
      errObj = await res.json();
    } catch {
      errObj = { error: res.statusText || `HTTP ${res.status}` };
    }

    // Create an error with the structured data attached
    const error = new Error(errObj.error || errObj.errors?.map(e => e.msg).join('. ') || res.statusText);
    error.data = errObj;
    error.status = res.status;
    throw error;
  }

  const data = await res.json();

  if (isCacheable) {
    cache.set(cacheKey, { timestamp: Date.now(), data });
  }

  return data;
}

// ─── AUTH ──────────────────────────────────────────────────────────────────────

/** Returns the currently logged-in user from the backend session (backed by Firebase Auth). */
export const fetchCurrentUser = async () => {
  try {
    const data = await apiFetch('/auth/me');
    return data.user ?? null;
  } catch {
    return null;
  }
};

/** Authenticates the user with username and password. */
export const login = async (username, password) => {
  clearToken();
  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  saveToken(data.token);
  return data;
};

/** Registers a new user with username and password. */
export const register = async (username, password, displayName) => {
  clearToken();
  const data = await apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, password, displayName }),
  });
  saveToken(data.token);
  return data;
};

/** Authenticates the user as an anonymous guest. */
export const loginAnonymously = async () => {
  const data = await apiFetch('/auth/anonymous', { method: 'POST' });
  saveToken(data.token);
  return data;
};

/** Verifies a Firebase Phone Auth ID token and logs in (or creates) the user. */
export const verifyFirebasePhone = async (idToken) => {
  const data = await apiFetch('/auth/phone/firebase-verify', { method: 'POST', body: JSON.stringify({ idToken }) });
  saveToken(data.token);
  return data;
};

/** Logs in an existing user with phone number + password. */
export const loginPhone = async (phone, password) => {
  clearToken();
  const data = await apiFetch('/auth/login-phone', { method: 'POST', body: JSON.stringify({ phone, password }) });
  saveToken(data.token);
  return data;
};

/** Resets password for an existing user verified via phone OTP. */
export const resetPasswordPhone = async (idToken, password) => {
  clearToken();
  const data = await apiFetch('/auth/reset-password-phone', { method: 'POST', body: JSON.stringify({ idToken, password }) });
  saveToken(data.token);
  return data;
};

/** Registers a new user using a verified Firebase phone idToken + chosen password. */
export const registerPhone = async (idToken, password) => {
  clearToken();
  const data = await apiFetch('/auth/register-phone', { method: 'POST', body: JSON.stringify({ idToken, password }) });
  saveToken(data.token);
  return data;
};

/** Destroys the backend session (Firebase sign-out is handled server-side). */
export const logout = async () => {
  clearToken();
  return apiFetch('/auth/logout', { method: 'POST' });
};

/** Permanently deletes the authenticated user's account. */
export const deleteAccount = async () => {
  clearToken();
  return apiFetch('/auth/account', { method: 'DELETE' });
};

// ─── PROFILE ───────────────────────────────────────────────────────────────────

/** Update the authenticated user's profile stored in Firestore via backend. */
export const updateProfile = async (profileData) => {
  return apiFetch('/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  });
};

/** Fetch a public profile by userId (Firestore document) via backend. */
export const fetchProfile = async (userId) => {
  return apiFetch(`/profile/${userId}`);
};

// ─── REVIEWS ───────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated/filtered list of reviews from Firestore via backend.
 * @param {Object} params  - { status, sort, search, limit, cursor }
 */
export const fetchReviews = async (params = {}) => {
  const query = new URLSearchParams(params);
  return apiFetch(`/reviews?${query.toString()}`);
};

/** Fetch a single review by its Firestore document ID. */
export const fetchReviewById = async (id) => {
  return apiFetch(`/reviews/${id}`);
};

/** Fetch only the currently logged-in user's own reviews. */
export const fetchMyReviews = async () => {
  return apiFetch('/reviews/mine');
};

/**
 * Submit a new interview review.  The backend writes it to Firestore.
 * @param {Object} reviewData
 */
export const submitReview = async (reviewData) => {
  return apiFetch('/reviews', {
    method: 'POST',
    body: JSON.stringify(reviewData),
  });
};

/**
 * Update an existing review (edit flow).
 * @param {string} id
 * @param {Object} reviewData
 */
export const updateReview = async (id, reviewData) => {
  return apiFetch(`/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(reviewData),
  });
};

/**
 * Delete a review by its Firestore document ID.
 * @param {string} id
 */
export const deleteReview = async (id) => {
  return apiFetch(`/reviews/${id}`, { method: 'DELETE' });
};

/** Upvote a review — backend increments the counter in Firestore. */
export const upvoteReview = async (id) => {
  return apiFetch(`/reviews/${id}/upvote`, { method: 'POST' });
};

// ─── COMPANIES ─────────────────────────────────────────────────────────────────

/**
 * Fetch a list of companies aggregated from Firestore reviews via backend.
 * @param {Object} params  - { limit, sort }
 */
export const fetchCompanies = async (params = {}) => {
  const query = new URLSearchParams(params);
  return apiFetch(`/companies?${query.toString()}`);
};

/**
 * Fetch a single company detail page (company meta + its reviews) via backend.
 * @param {string} slug  - URL-friendly company name, e.g. "tata-consultancy-services"
 */
export const fetchCompanyBySlug = async (slug) => {
  return apiFetch(`/companies/${slug}`);
};

// ─── STATS ─────────────────────────────────────────────────────────────────────

/**
 * Fetch platform-wide aggregated stats (totalReviews, ghostRate, offerRate …).
 * Backend computes these from Firestore aggregation queries.
 */
export const fetchStats = async () => {
  return apiFetch('/stats');
};

// ─── TICKER ────────────────────────────────────────────────────────────────────

/**
 * Fetch live ticker items (recent activity) from Firestore via backend.
 */
export const fetchTicker = async () => {
  return apiFetch('/ticker');
};

// ─── SALARIES ──────────────────────────────────────────────────────────────────

/**
 * Fetch salary benchmark data aggregated from Firestore reviews via backend.
 * @param {Object} params  - { limit, company, role }
 */
export const fetchSalaries = async (params = {}) => {
  const query = new URLSearchParams(params);
  return apiFetch(`/salaries?${query.toString()}`);
};

// ─── LEADERBOARD ───────────────────────────────────────────────────────────────

/**
 * Fetch top contributors leaderboard from Firestore via backend.
 * @param {Object} params  - { limit }
 */
export const fetchLeaderboard = async (params = {}) => {
  const query = new URLSearchParams(params);
  return apiFetch(`/leaderboard?${query.toString()}`);
};

// ─── NOTIFICATIONS ─────────────────────────────────────────────────────────────

export const fetchNotifications = async () => {
  return apiFetch('/notifications');
};

export const markNotificationRead = async (id) => {
  return apiFetch(`/notifications/${id}/read`, { method: 'POST' });
};

export const markAllNotificationsRead = async () => {
  return apiFetch('/notifications/read-all', { method: 'POST' });
};

// ─── TOP QUESTIONS ─────────────────────────────────────────────────────────────

export const fetchTopQuestions = async (limit = 25) => {
  return apiFetch(`/questions/top?limit=${limit}`);
};

export const fetchReviewsByIds = async (ids = []) => {
  if (!ids.length) return { reviews: [] };
  return apiFetch(`/questions/reviews?ids=${ids.join(',')}`);
};

// ─── SAVED KAHANIS ─────────────────────────────────────────────────────────────

export const fetchSavedKahanis = async () => {
  return apiFetch('/profile/saved');
};

export const saveKahani = async (reviewId) => {
  return apiFetch(`/profile/save/${reviewId}`, { method: 'POST' });
};

export const unsaveKahani = async (reviewId) => {
  return apiFetch(`/profile/save/${reviewId}`, { method: 'DELETE' });
};

// ─── COMMENTS ──────────────────────────────────────────────────────────────────

export const fetchComments = async (reviewId) => {
  return apiFetch(`/reviews/${reviewId}/comments`);
};

export const addComment = async (reviewId, text) => {
  return apiFetch(`/reviews/${reviewId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
};

export const editComment = async (reviewId, commentId, text) => {
  return apiFetch(`/reviews/${reviewId}/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ text }),
  });
};

export const deleteComment = async (reviewId, commentId) => {
  return apiFetch(`/reviews/${reviewId}/comments/${commentId}`, { method: 'DELETE' });
};

export const addReply = async (reviewId, commentId, text) => {
  return apiFetch(`/reviews/${reviewId}/comments/${commentId}/replies`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
};

export const reactToComment = async (reviewId, commentId, emoji) => {
  return apiFetch(`/reviews/${reviewId}/comments/${commentId}/react`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
};

export const reactToReply = async (reviewId, commentId, replyId, emoji) => {
  return apiFetch(`/reviews/${reviewId}/comments/${commentId}/replies/${replyId}/react`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
};

// ─── ADMIN ─────────────────────────────────────────────────────────────────────

export const fetchAdminStats = async () => apiFetch('/admin/stats');
export const fetchAdminReviews = async (params = {}) => apiFetch(`/admin/reviews?${new URLSearchParams(params)}`);
export const adminDeleteReview = async (id) => apiFetch(`/admin/reviews/${id}`, { method: 'DELETE' });
export const fetchAdminUsers = async (params = {}) => apiFetch(`/admin/users?${new URLSearchParams(params)}`);
export const adminDeleteUser = async (id) => apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
export const fetchAdminActivity = async (params = {}) => apiFetch(`/admin/activity?${new URLSearchParams(params)}`);

export const linkEmail = async (email) =>
  apiFetch('/auth/link-email', { method: 'POST', body: JSON.stringify({ email }) });

export const linkPhone = async (idToken) =>
  apiFetch('/auth/link-phone', { method: 'POST', body: JSON.stringify({ idToken }) });
